import React from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { SendHorizontal } from 'lucide-react';

export interface ChatThreadMessage {
  id: string;
  /** Which side of the thread the message belongs to. */
  sender: 'researcher' | 'participant';
  text: string;
  createdAt: string;
  /** Delivery receipt for researcher messages; 'read' renders the "Seen" indicator. */
  status?: 'sent' | 'read';
  /** The ciphertext did not decrypt on this device; `text` holds the placeholder copy. */
  undecryptable?: boolean;
  /** The send request failed; the bubble stays with a failure caption. */
  failed?: boolean;
  /** A system line (security event) rendered centered between the bubbles; `text` is the copy. */
  system?: boolean;
}

export interface ChatThreadNotice {
  severity: 'info' | 'warning' | 'error';
  title: string;
  body?: React.ReactNode;
}

export interface ChatThreadProps {
  /** Participant display label, used in aria labels and the composer placeholder. */
  title: string;
  messages: ChatThreadMessage[];
  /** The single message that renders the "Seen" caption (the last own read message). */
  seenMessageId?: string | null;
  /** Replaces the thread with a centered spinner (keys or first page still loading). */
  loading?: boolean;
  /** Banner above the thread (access pending / participant app outdated / errors). */
  notice?: ChatThreadNotice | null;
  /** Plain text shown where the messages go while the thread is empty (default "No messages yet."). */
  emptyText?: string;
  /** Optional conversation-scoped control rendered beside the participant title. */
  headerAction?: React.ReactNode;
  composerDisabled?: boolean;
  hasMore?: boolean;
  loadingOlder?: boolean;
  onLoadOlder?: () => void;
  onSend: (text: string) => void;
}

const timeFormatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });

/** A centered system line (key change, member joined/left). */
function SystemLine({ message }: { message: ChatThreadMessage }) {
  return (
    <Typography
      role="note"
      variant="caption"
      sx={{ alignSelf: 'center', color: 'text.secondary', textAlign: 'center', px: 1 }}
    >
      {message.text} · {timeFormatter.format(new Date(message.createdAt))}
    </Typography>
  );
}

/** One thread bubble. Researcher messages align right in cardinal; participant messages align left. */
function ThreadBubble({ message, showSeen }: { message: ChatThreadMessage; showSeen: boolean }) {
  const isResearcher = message.sender === 'researcher';
  return (
    <Box
      sx={{
        alignSelf: isResearcher ? 'flex-end' : 'flex-start',
        maxWidth: '78%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isResearcher ? 'flex-end' : 'flex-start',
      }}
    >
      <Box
        sx={{
          bgcolor: isResearcher ? 'primary.main' : 'grey.100',
          color: isResearcher ? 'primary.contrastText' : 'text.primary',
          px: 2,
          py: 1.1,
          borderRadius: '16px',
          ...(isResearcher
            ? { borderBottomRightRadius: '4px', boxShadow: '0 1px 2px rgba(140,21,21,0.25)' }
            : { borderBottomLeftRadius: '4px' }),
          ...(message.undecryptable ? { opacity: 0.7 } : {}),
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'inherit',
            ...(message.undecryptable ? { fontStyle: 'italic' } : {}),
          }}
        >
          {message.text}
        </Typography>
      </Box>
      <Typography
        variant="caption"
        sx={{ color: message.failed ? 'error.main' : 'text.secondary', mt: 0.25 }}
      >
        {message.failed
          ? 'Failed to send'
          : timeFormatter.format(new Date(message.createdAt))}
        {!message.failed && showSeen && message.status === 'read' && ' · Seen'}
      </Typography>
    </Box>
  );
}

/**
 * The active message thread plus composer. Purely presentational: the container
 * decrypts messages, decides receipts (`seenMessageId`), pagination, and every
 * disabled/banner state; this component only lays them out.
 */
export function ChatThread({
  title,
  messages,
  seenMessageId,
  loading = false,
  notice,
  emptyText,
  headerAction,
  composerDisabled = false,
  hasMore = false,
  loadingOlder = false,
  onLoadOlder,
  onSend,
}: ChatThreadProps) {
  const [draft, setDraft] = React.useState('');
  const listRef = React.useRef<HTMLDivElement>(null);
  const lastMessageId = messages[messages.length - 1]?.id;

  // Newest row in view after the first page and every append; a prepended older page keeps its place.
  React.useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [lastMessageId, loading]);

  const sendDraft = () => {
    const text = draft.trim();
    if (!text || composerDisabled) return;
    onSend(text);
    setDraft('');
  };

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          minHeight: 52,
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {headerAction}
      </Box>
      {notice && (
        <Alert severity={notice.severity} sx={{ m: 1.5, mb: 0 }}>
          <AlertTitle sx={{ fontSize: 14, mb: notice.body ? 0.5 : 0 }}>{notice.title}</AlertTitle>
          {notice.body}
        </Alert>
      )}

      <Box
        ref={listRef}
        aria-label={`Messages with ${title}`}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25, px: 2.5, py: 2 }}
      >
        {loading ? (
          <CircularProgress size={28} sx={{ m: 'auto' }} aria-label="Loading messages" />
        ) : (
          <>
            {hasMore && (
              <Button
                size="small"
                onClick={onLoadOlder}
                disabled={loadingOlder}
                sx={{ alignSelf: 'center', textTransform: 'none', color: 'text.secondary' }}
              >
                {loadingOlder ? 'Loading…' : 'Load earlier messages'}
              </Button>
            )}
            {messages.length === 0 && !notice && (
              <Typography variant="body2" sx={{ color: 'text.secondary', m: 'auto' }}>
                {emptyText ?? 'No messages yet.'}
              </Typography>
            )}
            {messages.map((message) =>
              message.system ? (
                <SystemLine key={message.id} message={message} />
              ) : (
                <ThreadBubble key={message.id} message={message} showSeen={message.id === seenMessageId} />
              ),
            )}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={`Message ${title}…`}
          value={draft}
          disabled={composerDisabled}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              sendDraft();
            }
          }}
        />
        <IconButton
          aria-label="Send message"
          color="primary"
          onClick={sendDraft}
          disabled={!draft.trim() || composerDisabled}
        >
          <SendHorizontal size={20} />
        </IconButton>
      </Box>
    </Box>
  );
}
